import { CONSTANTS } from './constants';

export interface IImportedAssignmentItem {
  fullNameFromContractor: string | null;
  phoneNumber: string;
  lockerNumber?: string | null;
  sBSubscriptionVoucher?: boolean;
  upgradeToSmartLockerVoucher?: boolean;
}

const phoneRegex =
  CONSTANTS.REGEX_PATTERN_VALIDATORS
    .PHONE_NUMBER_WITH_OR_WITHOUT_ISRAEL_COUNTRY_CODE;

const normalizePhone = (value: string): string => {
  let v = value.trim();
  if (v.startsWith('+972')) {
    v = v.slice(4);
  }
  if (!v.startsWith('0')) v = '0' + v;
  return v;
};

const toBool = (value: any): boolean => {
  if (value == null) return false;
  const s = String(value).trim().toLowerCase();
  return s === 'v';
};

// Build headers via translation function to keep component lean
export const getCustomerAssignmentsHeaders = (
  rcTranslate: (k: string) => string
) => ({
  FULL_NAME: rcTranslate(
    'customerAssignments.insertRange.excelHeaders.fullNameFromContractor'
  ),
  PHONE_NUMBER: rcTranslate(
    'customerAssignments.insertRange.excelHeaders.phoneNumber'
  ),
  LOCKER_NUMBER: rcTranslate(
    'customerAssignments.insertRange.excelHeaders.lockerNumber'
  ),
  SB_SUBSCRIPTION_VOUCHER: rcTranslate(
    'customerAssignments.insertRange.excelHeaders.sBSubscriptionVoucher'
  ),
  UPGRADE_TO_SMART_LOCKER_VOUCHER: rcTranslate(
    'customerAssignments.insertRange.excelHeaders.upgradeToSmartLockerVoucher'
  ),
});

export async function readCustomerAssignmentsFile(
  file: File,
  rcTranslate: (k: string) => string
): Promise<IImportedAssignmentItem[]> {
  try {
    const XLSX = await import('xlsx');
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1 });
    const [header, ...rest] = rows;
    const headers = getCustomerAssignmentsHeaders(rcTranslate);
    const idx = (name: string) =>
      header.findIndex((h) => h.trim() === name) ?? -1;
    const phoneIdx = idx(headers.PHONE_NUMBER);
    if (phoneIdx === -1) {
      console.error('phoneNumber column missing');
      return [];
    }
    const fullNameIdx = idx(headers.FULL_NAME);
    const lockerNumberIdx = idx(headers.LOCKER_NUMBER);
    const sbSubVoucherIdx = idx(headers.SB_SUBSCRIPTION_VOUCHER);
    const smartLockerVoucherIdx = idx(headers.UPGRADE_TO_SMART_LOCKER_VOUCHER);
    const items: IImportedAssignmentItem[] = rest
      .filter((r) => {
        const rawPhone = String(r[phoneIdx]);
        if (rawPhone == null) return false;
        const phone = normalizePhone(rawPhone);
        return phoneRegex.test(phone);
      })
      .map((r) => {
        const rawPhone = String(r[phoneIdx]);
        const phone = normalizePhone(rawPhone);
        return {
          phoneNumber: phone,
          fullNameFromContractor: fullNameIdx > -1 && r[fullNameIdx] != null
              ? String(r[fullNameIdx]).trim()
              : null,
          lockerNumber:
            lockerNumberIdx > -1 && r[lockerNumberIdx] != null
              ? String(r[lockerNumberIdx]).trim()
              : null,
          sBSubscriptionVoucher:
            sbSubVoucherIdx > -1 ? toBool(r[sbSubVoucherIdx]) : false,
          upgradeToSmartLockerVoucher:
            smartLockerVoucherIdx > -1
              ? toBool(r[smartLockerVoucherIdx])
              : false,
        };
      });
    return items;
  } catch (err) {
    console.error('Failed parsing assignments file', err);
    return [];
  }
}

export async function generateCustomerAssignmentsExampleURL(
  rcTranslate: (k: string) => string
): Promise<string> {
  const XLSX = await import('xlsx');
  const headersObj = getCustomerAssignmentsHeaders(rcTranslate);
  const headers = [
    headersObj.FULL_NAME,
    headersObj.PHONE_NUMBER,
    headersObj.LOCKER_NUMBER,
    headersObj.SB_SUBSCRIPTION_VOUCHER,
    headersObj.UPGRADE_TO_SMART_LOCKER_VOUCHER,
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Example');
  const targetWidthPx = 160;
  (ws as any)['!cols'] = headers.map(() => ({ wpx: targetWidthPx }));
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  return URL.createObjectURL(blob);
}
